import { useLocation, useNavigate } from "react-router-dom";
import { JSX, useEffect, useState } from "react";
import { Staff } from "../../types/staff";
import { EditModeProvider, useEditMode } from "../../context/EditModeContext";
import useUrlTab from "../../util/hooks/useUrlTab";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import ClassAccess from "../staff/class-access/ClassAccess";
import AssignSubject from "../staff/assign-subject/AssignSubject";
import Permissions from "../staff/permissions/Permissions";
import AdminFeatures from "../staff/permissions/AdminFeatures";
import { CustomButton, PageHeader } from "../../components";
import Tabs from "../../components/Tabs";
import ProfileInformation from "./ProfileInformation";
import PersonalInfo from "./PersonalInfo";
import FinancialInfo from "./FinancialInfo";
import DocumentInfo from "./DocumantInfo";
import ExitInfo from "./ExitInfo";
import SubButton from "../../components/SubButtons";
import { idcard } from "../../asset";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import StaffApis from "../../api/staffApis";
import { toast } from "sonner";
import { IS_LIVE } from "../../util/config/env";

const tabOptions = [
  "My Details",
  "Class Access",
  "Assigned Subjects",
  "Permissions",
];

function StaffProfileContent({
  staffData,
  fetchProfile,
  setFormData,
}: {
  staffData?: Staff;
  fetchProfile: () => Promise<void>;
  setFormData;
}) {
  const { isEditing, toggleEdit } = useEditMode();
  const { activeTab, handleTabChange } = useUrlTab(tabOptions);
  const { confirm, modal } = useConfirmToast();
  const navigate = useNavigate();
  const staffId = staffData?.staffId;

  useEffect(() => {
    setFormData(staffData);
  }, [staffData]);

  const handleChildChange = (updatedFields: Partial<Staff>) => {
    setFormData((prev) => (prev ? { ...prev, ...updatedFields } : prev));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!staffData?.staffId) return;

    const { staffId, academicCycles, features, ...cleanData } = staffData;

    const payload = {
      ...cleanData,
      academicCycles: [], 
      features: [], 
    };

    try {
      const result = await StaffApis.updateStaff(staffData.staffId, payload);

      if (result) {
        toast.success("Staff profile updated successfully");
        toggleEdit(); // Turn off edit mode
      }
    } catch (error) {
      toast.error("Failed to update staff details");
    } finally {
    }
  };

  const tabContent: Record<string, JSX.Element> = {
    "My Details": (
      <>
        <ProfileInformation staff={staffData} onChange={handleChildChange} />
        <PersonalInfo staff={staffData} onChange={handleChildChange} />
        <FinancialInfo staff={staffData} onChange={handleChildChange} />
        <div>
          <DocumentInfo staff={staffData} onChange={handleChildChange} />
          <div className="flex justify-end mb-3 px-4 items-end relative group">
            <CustomButton
              color="bg-primary-500"
              text="Submit"
              type="submit"
              disabled={!isEditing}
              onClick={handleUpdate}
            />
          </div>
        </div>
        <ExitInfo staff={staffData} />
      </>
    ),
    "Class Access": (
      <div className="w-full overflow-x-auto">
        <div className="min-w-[800px] max-w-6xl">
          <ClassAccess
            staffId={staffId}
            academicCycles={staffData?.academicCycles || []}
            onRefresh={fetchProfile}
          />
        </div>
      </div>
    ),
    "Assigned Subjects": (
      <div className="w-full overflow-x-auto">
        <div className="min-w-[400px] max-w-3xl">
          <AssignSubject staffId={staffId} />{" "}
        </div>
      </div>
    ),
    Permissions: (
      <div className="w-full flex flex-col gap-8 overflow-x-auto">
        <div className="w-full overflow-x-auto">
          <div className="min-w-[800px] max-w-6xl">
            <Permissions
              staffId={staffId}
              features={staffData?.features || []}
              onRefresh={fetchProfile}
            />
          </div>
        </div>
      </div>
    ),
  };

  return (
    <div className="flex flex-col gap-5 overflow-hidden">
      <div className="flex flex-col md:flex-row gap-4 justify-between md:items-center">
        <PageHeader title="My Profile" showBack />
        {!IS_LIVE && (
          <SubButton
            text="ID Card"
            color="bg-success-600"
            icon={idcard}
            onClick={() =>
              navigate(`/staff/staff-profile/staff-id-card/${staffData?.staffId}`)
            }
          />
        )}
      </div>

      <div className="bg-white rounded-lg w-full grid py-5 divide-y divide-neutral-50">
        <div className="w-full flex flex-col lg:flex-row justify-between gap-4 lg:items-center overflow-x-auto lg:overflow-visible">
          <div className="lg:flex-1 flex justify-between items-center px-7">
            <Tabs
              tabs={tabOptions}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />

            {activeTab === "My Details" && (
              <div className="flex justify-end items-end">
                <button
                  onClick={toggleEdit}
                  className={`${isEditing ? "bg-error-500" : "bg-warning-600"} py-1 px-5 rounded font-semibold text-[13px] text-white`}
                >
                  {isEditing ? "Cancel" : "Edit"}
                </button>
              </div>
            )}
          </div>
          <div className="flex justify-end items-end px-7">
            {activeTab === "Employee Details" && (
              <div className="flex justify-end items-end px-7">
                <button
                  onClick={toggleEdit}
                  className={`${isEditing ? "bg-error-500" : "bg-warning-600"} py-1 px-5 rounded font-semibold text-[13px] text-white`}
                >
                  {isEditing ? "Cancel" : "Edit"}
                </button>
              </div>
            )}
          </div>
        </div>
        {tabContent[activeTab]}
      </div>
      {modal}
    </div>
  );
}

export default function ViewStaffProfile() {
  const user = useSelector((state: RootState) => state.user);
  const [staffData, setStaffData] = useState<Staff | undefined>();
  const [loading, setLoading] = useState(true);

  const fetchProfile = async () => {
    const staffId = user?.user?.staff?.staffId;
    if (!staffId) return;
    try {
      const response = await StaffApis.getStaffById(staffId);
      setStaffData(response?.data);
    } catch {
      console.error("Failed to fetch profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.user?.staff?.staffId) {
      fetchProfile();
    }
  }, [user?.user?.staff?.staffId]);

  if (loading) return <div className="p-6">Loading profile...</div>;

  return (
    <EditModeProvider>
      <StaffProfileContent
        staffData={staffData}
        setFormData={setStaffData}
        fetchProfile={fetchProfile}
      />
    </EditModeProvider>
  );
}
